class MultiColumnSwiper extends HTMLElement {
  constructor() {
    super()
    this.Init()
  }

  Init() {
    new Swiper(this.querySelector(".swiper"), {
      slidesPerView: parseFloat(this.dataset.slides_per_row_mobile),
      spaceBetween: 0,
      navigation: {
        prevEl: this.querySelector(".prev-next-button--prev"),
        nextEl: this.querySelector(".prev-next-button--next")
      },
      on: {
        init: this.Update.bind(this),
        resize: this.Update.bind(this)
      },
      breakpoints: {
        768: {
          slidesPerView: parseFloat(this.dataset.slides_per_row_mobile) * 2
        },
        1024: {
          slidesPerView: parseFloat(this.dataset.slides_per_row_mobile) * 1.5,
          slidesPerGroup: 3
        },
        1280: {
          slidesPerView: parseFloat(this.dataset.slides_per_row),
          slidesPerGroup: 3
        }
      }
    })
  }

  Update() {
    const slidesPerRowMobile = parseFloat(this.dataset.slides_per_row_mobile)
    const slidesPerRow = parseFloat(this.dataset.slides_per_row)
    const isMobile = window.matchMedia("(max-width: 767px)").matches
    const isTablet = window.matchMedia("(min-width: 768px) and (max-width: 1024px)").matches
    const isDesktop = window.matchMedia("(min-width: 1280px)").matches
    const slidesCount = this.querySelectorAll(".swiper-slide").length

    if (isMobile) {
      this.classList.toggle("justify-center", slidesCount <= slidesPerRowMobile)
    }

    if (isTablet) {
      this.classList.toggle("justify-center", slidesCount <= slidesPerRowMobile * 2)
    }

    if (isDesktop) {
      this.classList.toggle("justify-center", slidesCount <= slidesPerRow)
    }
  }
}

customElements.define("multi-column-swiper", MultiColumnSwiper)

const u_debounce = (fn, delay) => {
  let timer

  return function (...args) {
    clearTimeout(timer)
    timer = setTimeout(() => fn.apply(this, args), delay)
  }
}

if (!customElements.get("stringer-options")) {
  class StringerOptions extends HTMLElement {
    connectedCallback() {
      this._radioName = this.dataset.radioName
      this._onChange = this._onChange.bind(this)

      this.addEventListener("change", this._onChange)
      this._syncSuffixesToSurfboardVariants()
    }

    disconnectedCallback() {
      this.removeEventListener("change", this._onChange)
    }

    _getSurfboardVariants() {
      return (
        this.closest(".product")?.querySelector("surfboard-variants") ||
        document.querySelector("surfboard-variants")
      )
    }

    _syncSuffixesToSurfboardVariants() {
      const surfboardVariants = this._getSurfboardVariants()

      if (surfboardVariants && this.dataset.stringerSuffixes) {
        surfboardVariants.dataset.stringerSuffixes = this.dataset.stringerSuffixes
      }
    }

    _onChange(event) {
      const input = event.target

      if (!(input instanceof HTMLInputElement)) return
      if (input.type !== "radio") return
      if (input.name !== this._radioName) return
      if (!input.checked) return

      this.querySelectorAll(".stringer-options__choice").forEach((label) => {
        const radio = label.querySelector('input[type="radio"]')
        label.classList.toggle("is-selected", Boolean(radio?.checked))
      })

      const model = input.dataset.model
      const surfboardVariants = this._getSurfboardVariants()

      if (!model || !surfboardVariants) return

      surfboardVariants.ChangeModel(model)
    }
  }

  customElements.define("stringer-options", StringerOptions)
}

if (!customElements.get("surfboard-variants")) {
  let surfboardProductLoadSeq = 0

  class SurfboardVariants extends HTMLElement {
    constructor() {
      super()
      this._surfboardsLoadSeq = 0
      this._allSurfboardOptions = []
      this._productHtmlCache = new Map()
    }

    connectedCallback() {
      this.model = this.dataset.model

      if (!this.model) return

      this.SetProductLoading(true)

      this._debouncedPopoverPick = u_debounce(
        (option) => this._handlePopoverOptionPick(option),
        300
      )

      this.LoadSurfboards()
    }

    GetBaseModel(model) {
      return (model || "").split(" - ")[0].trim()
    }

    GetStringerLabel(model) {
      const stringerOptions = this.GetStringerOptions()
      const defaultLabel = stringerOptions?.dataset.stringerOptionDefaultLabel?.trim() || "Single"
      const parts = (model || "").split(" - ")

      if (parts.length <= 1) {
        return defaultLabel
      }

      return parts
        .slice(1)
        .join(" - ")
        .replace(/\s*stringer\s*/gi, "")
        .trim()
    }

    GetRelatedModels(model) {
      const baseModel = this.GetBaseModel(model)

      const suffixes = (this.dataset.stringerSuffixes || "")
        .split(/\r?\n|,/)
        .map((suffix) => suffix.trim())
        .filter(Boolean)

      return [baseModel, ...suffixes.map((suffix) => `${baseModel} - ${suffix}`)]
    }

    async ChangeModel(newModel) {
      if (!newModel || newModel === this.model) return

      const previousModel = this.model
      const previousProductHandle = this.dataset.currentProductHandle
      const selectedOption = this.GetFirstAvailableOptionForModel(newModel)

      if (!selectedOption) {
        console.error("No surfboard option found for selected stringer")
        this.SyncActiveStringerOption()
        return
      }

      if (!this._productHtmlCache.has(selectedOption.dataset.productHandle)) {
        this.SetProductLoading(true)
      }

      this.model = newModel
      this.dataset.model = newModel

      this.RenderSizeOptionsForCurrentModel()
      this.RenderSelectedOption(selectedOption)
      this.SyncActiveStringerOption()

      const productLoaded = await this.LoadProduct(
        selectedOption.dataset.productHandle,
        selectedOption.dataset.length,
        this.dataset.sectionId
      )

      if (!productLoaded) {
        this.model = previousModel
        this.dataset.model = previousModel
        this.dataset.currentProductHandle = previousProductHandle
        this.RenderSizeOptionsForCurrentModel()
        this.SyncActiveStringerOption()
      }
    }

    SetProductLoading(isLoading) {
      const productInfo = this.closest(".product")?.querySelector(".product-info")
      if (!productInfo) return

      productInfo.classList.toggle("is-surfboard-loading", isLoading)
    }

    async LoadSurfboards() {
      const loadSeq = ++this._surfboardsLoadSeq
      const models = this.GetRelatedModels(this.model)

      try {
        const docs = await Promise.all(
          models.map((model) => this.FetchSurfboardPickerDocument(model))
        )

        if (loadSeq !== this._surfboardsLoadSeq) return

        const firstPicker = docs[0]?.querySelector("surfboard-picker")

        if (!firstPicker) {
          console.error("surfboard-picker not found")
          this.SetProductLoading(false)
          return
        }

        const firstList = firstPicker.querySelector(".popover__value-list")

        if (!firstList) {
          console.error("surfboard picker option list not found")
          this.SetProductLoading(false)
          return
        }

        const optionsMap = new Map()

        docs.forEach((doc) => {
          doc.querySelectorAll(".popover__value-option").forEach((option) => {
            const productHandle = option.dataset.productHandle
            if (!productHandle) return

            if (!optionsMap.has(productHandle)) {
              optionsMap.set(productHandle, option.cloneNode(true))
            }
          })
        })

        this._allSurfboardOptions = [...optionsMap.values()]

        this.innerHTML = ""
        this.appendChild(firstPicker)

        this.RenderSizeOptionsForCurrentModel()
        this.SyncStringerOptionsFromPicker()
        this.SelectFirstAvailableOptionForCurrentModel({ loadProduct: true })
        this.SyncActiveStringerOption()
      } catch (error) {
        console.error("Unable to load surfboard variants", error)
        this.SetProductLoading(false)
      }
    }

    RenderSizeOptionsForCurrentModel() {
      const list = this.querySelector(".popover__value-list")
      if (!list) return

      list.innerHTML = ""

      this._allSurfboardOptions.forEach((option) => {
        if (option.dataset.model !== this.model) return

        const clonedOption = option.cloneNode(true)
        clonedOption.hidden = false
        clonedOption.style.display = ""

        list.appendChild(clonedOption)
      })

      this.InitEventHandlers()
    }

    async FetchSurfboardPickerDocument(model) {
      const url = `/collections/all?filter.p.m.surfboard.model=${encodeURIComponent(model)}&section_id=dac-api-surfboard-variants&ts=${Date.now()}`
      const res = await fetch(url)

      if (!res.ok) {
        throw new Error(`Failed to fetch surfboard model: ${model}`)
      }

      const html = await res.text()

      return new DOMParser().parseFromString(html, "text/html")
    }

    GetFirstAvailableOptionForModel(model) {
      return (
        this._allSurfboardOptions.find(
          (option) =>
            option.dataset.model === model &&
            option.dataset.productHandle === this.dataset.currentProductHandle
        ) ||
        this._allSurfboardOptions.find(
          (option) => option.dataset.model === model && option.dataset.available === "true"
        ) ||
        this._allSurfboardOptions.find((option) => option.dataset.model === model)
      )
    }

    SelectFirstAvailableOptionForCurrentModel({ loadProduct = true } = {}) {
      const selectedOption =
        this.querySelector(
          `.popover__value-option[data-model="${CSS.escape(this.model)}"][data-product-handle="${CSS.escape(this.dataset.currentProductHandle || "")}"]`
        ) ||
        this.querySelector(
          `.popover__value-option[data-model="${CSS.escape(this.model)}"][data-available="true"]`
        ) ||
        this.querySelector(`.popover__value-option[data-model="${CSS.escape(this.model)}"]`)

      if (!selectedOption) {
        console.error("No surfboard option found for current model")
        this.SetProductLoading(false)
        return
      }

      if (selectedOption.dataset.available === "false") {
        console.error("Selected surfboard option is unavailable")
        this.SetProductLoading(false)
        return
      }

      this.RenderSelectedOption(selectedOption)

      if (loadProduct) {
        this.LoadProduct(
          selectedOption.dataset.productHandle,
          selectedOption.dataset.length,
          this.dataset.sectionId
        )
      }
    }

    RenderSelectedOption(option) {
      if (!option) return

      const productHandle = option.dataset.productHandle

      if (!productHandle) {
        console.error("Product handle not found")
        return
      }

      this.dataset.currentProductHandle = productHandle
    }

    UpdateSelectedSizeLabel(length) {
      const selectedLabel = this.querySelector("#popover-surboards-selected-value")
      if (!selectedLabel || !length) return

      const selectedOption = this.querySelector(
        `.popover__value-option[data-length="${CSS.escape(length)}"][data-model="${CSS.escape(this.model)}"]`
      )

      const input = selectedOption?.querySelector("input")

      if (input) {
        selectedLabel.textContent = input.value
      }
    }

    SyncActiveStringerOption() {
      const stringerOptions = this.GetStringerOptions()
      if (!stringerOptions) return

      stringerOptions.querySelectorAll(".stringer-options__choice").forEach((label) => {
        const input = label.querySelector('input[type="radio"]')
        const isSelected = input?.dataset.model === this.model

        if (input) {
          input.checked = isSelected
        }

        label.classList.toggle("is-selected", isSelected)
      })
    }

    SyncStringerOptionsFromPicker() {
      const stringerOptions = this.GetStringerOptions()
      if (!stringerOptions) return

      const items = stringerOptions.querySelector(".stringer-options__items")
      if (!items) return

      const radioName = stringerOptions.dataset.radioName
      const baseModel = this.GetBaseModel(this.model)

      const existingModels = new Set(
        [...stringerOptions.querySelectorAll("input[data-model]")].map(
          (input) => input.dataset.model
        )
      )

      this._allSurfboardOptions.forEach((option, index) => {
        const model = option.dataset.model
        const productHandle = option.dataset.productHandle

        if (!model || !productHandle) return
        if (existingModels.has(model)) return
        if (this.GetBaseModel(model) !== baseModel) return

        const stringerLabel = this.GetStringerLabel(model)
        const id = `stringer-js-${baseModel.toLowerCase().replace(/\s+/g, "-")}-${index}`

        const label = document.createElement("label")
        label.className = "stringer-options__choice"
        label.setAttribute("for", id)

        const input = document.createElement("input")
        input.id = id
        input.className = "sr-only"
        input.type = "radio"
        input.name = radioName
        input.value = productHandle
        input.dataset.productHandle = productHandle
        input.dataset.model = model

        const span = document.createElement("span")
        span.className = "button button--outline button--sm"
        span.textContent = stringerLabel

        label.append(input, span)
        items.appendChild(label)

        existingModels.add(model)
      })

      stringerOptions.hidden =
        stringerOptions.querySelectorAll(".stringer-options__choice").length <= 1
    }

    GetStringerOptions() {
      return (
        this.closest(".product")?.querySelector("stringer-options") ||
        document.querySelector("stringer-options")
      )
    }

    InitEventHandlers() {
      this.querySelectorAll(".popover__value-option").forEach((option) => {
        option.addEventListener("click", () => this._debouncedPopoverPick(option))
      })
    }

    async _handlePopoverOptionPick(option) {
      if (!option || option.dataset.available === "false") return
      if (option.hidden || option.style.display === "none") return

      const productHandle = option.dataset.productHandle
      const length = option.dataset.length
      const optionModel = option.dataset.model
      const currentProductHandle = this.dataset.currentProductHandle
      const previousModel = this.model
      const previousProductHandle = this.dataset.currentProductHandle

      if (!productHandle) {
        console.error("Product not found")
        return
      }

      if (optionModel && optionModel !== this.model) {
        this.model = optionModel
        this.dataset.model = optionModel
      }

      if (productHandle === currentProductHandle) {
        this.RenderSelectedOption(option)

        const popover = this.querySelector("x-popover")
        if (popover) popover.hide()

        return
      }

      if (!this._productHtmlCache.has(productHandle)) {
        this.SetProductLoading(true)
      }
      this.RenderSelectedOption(option)

      const productLoaded = await this.LoadProduct(productHandle, length, this.dataset.sectionId)

      if (!productLoaded) {
        this.model = previousModel
        this.dataset.model = previousModel
        this.dataset.currentProductHandle = previousProductHandle
        this.RenderSizeOptionsForCurrentModel()
        this.SyncActiveStringerOption()
      }
    }

    RenderFromOption(option) {
      if (!option) return

      const surfboardMetafields = {
        model: option.dataset.model || "",
        tail_shape: option.dataset.tailShape || "",
        length_in: option.dataset.lengthIn || "",
        width_in: option.dataset.widthIn || "",
        thickness_in: option.dataset.thicknessIn || "",
        volume: option.dataset.volume || "",
        type: option.dataset.type || "",
        length: option.dataset.lengthLabel || "",
        id_surfboardmodel: option.dataset.idSurfboardmodel || "",
        id_warehouse: option.dataset.idWarehouse || "",
        construction_type: option.dataset.constructionType || "",
        stocktype: option.dataset.stocktype || ""
      }

      this.RenderSizeSummary(surfboardMetafields)
      this.UpdateTechSpecs(this.BuildMetafieldsMapFromOption(option))
    }

    BuildMetafieldsMapFromOption(option) {
      return {
        "surfboard.model": option.dataset.model || "",
        "surfboard.tail_shape": option.dataset.tailShape || "",
        "surfboard.length_in": option.dataset.lengthIn || "",
        "surfboard.width_in": option.dataset.widthIn || "",
        "surfboard.thickness_in": option.dataset.thicknessIn || "",
        "surfboard.volume": option.dataset.volume || "",
        "surfboard.type": option.dataset.type || "",
        "surfboard.length": option.dataset.lengthLabel || "",
        "surfboard.id_surfboardmodel": option.dataset.idSurfboardmodel || "",
        "surfboard.id_warehouse": option.dataset.idWarehouse || "",
        "surfboard.construction_type": option.dataset.constructionType || "",
        "surfboard.stocktype": option.dataset.stocktype || ""
      }
    }

    RenderSizeSummary(surfboardMetafields) {
      const sizeRowEl = document.querySelector("[data-surfboard-size-row]")
      const sizeSummaryEl = document.querySelector('[data-surfboard-field="size-summary"]')

      if (!sizeRowEl || !sizeSummaryEl) return

      const hasSizeData =
        surfboardMetafields.length_in ||
        surfboardMetafields.width_in ||
        surfboardMetafields.thickness_in ||
        surfboardMetafields.volume

      if (!hasSizeData) {
        sizeSummaryEl.textContent = ""
        sizeRowEl.style.display = "none"
        return
      }

      sizeSummaryEl.textContent = `${surfboardMetafields.length_in} X ${surfboardMetafields.width_in} X ${surfboardMetafields.thickness_in} - ${surfboardMetafields.volume}L`
      sizeRowEl.style.display = ""
    }

    UpdateTechSpecs(metafieldsMap) {
      document.querySelectorAll("[data-tech-mf-key]").forEach((el) => {
        const key = (el.dataset.techMfKey || "").trim()
        if (!key) return

        const incomingValue = metafieldsMap[key]

        if (typeof incomingValue === "undefined") return
        if (incomingValue === null || incomingValue === "") return

        el.textContent = incomingValue
      })
    }

    async LoadProduct(productHandle, length, sectionId) {
      const loadSeq = ++surfboardProductLoadSeq
      const currentProduct = this.closest(".product")

      if (!currentProduct) return false

      fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          attributes: {
            current_surfboard_length: length || null
          }
        })
      }).catch((error) => {
        console.error("Unable to update selected surfboard length", error)
      })

      if (loadSeq !== surfboardProductLoadSeq) return false

      let doc

      if (this._productHtmlCache.has(productHandle)) {
        doc = this._productHtmlCache.get(productHandle).cloneNode(true)
      } else {
        const url = `/products/${productHandle}?section_id=${sectionId}&ts=${Date.now()}`
        let res

        try {
          res = await fetch(url)
        } catch (error) {
          console.error(error)
          this.SetProductLoading(false)
          return false
        }

        if (!res.ok) {
          console.error(`Unable to load product: ${productHandle}`)
          this.SetProductLoading(false)
          return false
        }

        if (loadSeq !== surfboardProductLoadSeq) return false

        const html = await res.text()

        if (loadSeq !== surfboardProductLoadSeq) return false

        doc = new DOMParser().parseFromString(html, "text/html")

        this._productHtmlCache.set(productHandle, doc.cloneNode(true))
      }

      const newProduct = doc.querySelector("product-rerender .product")
      let metafieldsEl = doc.querySelector(".surfboard-metafields-data")

      if (!newProduct) {
        console.error(`Unable to find product content: ${productHandle}`)
        this.SetProductLoading(false)
        return false
      }

      if (!this.MetafieldsElementHasUsefulData(metafieldsEl)) {
        metafieldsEl = null
      }

      if (metafieldsEl) {
        this.RenderFromMetafieldsElement(metafieldsEl)
      }

      this.ReplaceProductContent(currentProduct, newProduct)
      this.UpdateSelectedSizeLabel(length)
      this.SetProductLoading(false)

      if (window.location.pathname.includes("/products/")) {
        this.UpdateUrlHistory(productHandle)
      }

      this.SyncActiveStringerOption()

      return true
    }

    MetafieldsElementHasUsefulData(metafieldsEl) {
      if (!metafieldsEl) return false

      return Boolean(
        metafieldsEl.dataset.model ||
        metafieldsEl.dataset.tailShape ||
        metafieldsEl.dataset.lengthIn ||
        metafieldsEl.dataset.widthIn ||
        metafieldsEl.dataset.thicknessIn ||
        metafieldsEl.dataset.volume ||
        metafieldsEl.dataset.type ||
        metafieldsEl.dataset.length ||
        metafieldsEl.dataset.idSurfboardmodel ||
        metafieldsEl.dataset.idWarehouse ||
        metafieldsEl.dataset.constructionType ||
        metafieldsEl.dataset.stocktype
      )
    }

    RenderFromMetafieldsElement(metafieldsEl) {
      const surfboardMetafields = {
        length_in: metafieldsEl.dataset.lengthIn || "",
        width_in: metafieldsEl.dataset.widthIn || "",
        thickness_in: metafieldsEl.dataset.thicknessIn || "",
        volume: metafieldsEl.dataset.volume || ""
      }

      this.RenderSizeSummary(surfboardMetafields)

      this.UpdateTechSpecs({
        "surfboard.model": metafieldsEl.dataset.model || "",
        "surfboard.tail_shape": metafieldsEl.dataset.tailShape || "",
        "surfboard.length_in": metafieldsEl.dataset.lengthIn || "",
        "surfboard.width_in": metafieldsEl.dataset.widthIn || "",
        "surfboard.thickness_in": metafieldsEl.dataset.thicknessIn || "",
        "surfboard.volume": metafieldsEl.dataset.volume || "",
        "surfboard.type": metafieldsEl.dataset.type || "",
        "surfboard.length": metafieldsEl.dataset.length || "",
        "surfboard.id_surfboardmodel": metafieldsEl.dataset.idSurfboardmodel || "",
        "surfboard.id_warehouse": metafieldsEl.dataset.idWarehouse || "",
        "surfboard.construction_type": metafieldsEl.dataset.constructionType || "",
        "surfboard.stocktype": metafieldsEl.dataset.stocktype || ""
      })
    }

    ReplaceProductContent(currentProduct, newProduct) {
      const newGallery = newProduct.querySelector(".product__gallery")
      const currentGallery = currentProduct.querySelector(".product__gallery")

      if (newGallery && currentGallery) {
        currentGallery.replaceWith(newGallery)
      }

      const blocksToReplace = [
        "text",
        "quantity-selector",
        "buy-buttons",
        "sku",
        "price",
        "vendor",
        "description"
      ]

      blocksToReplace.forEach((blockType) => {
        const currentBlock = currentProduct.querySelector(`[data-block-type="${blockType}"]`)
        const newBlock = newProduct.querySelector(`[data-block-type="${blockType}"]`)

        if (newBlock && currentBlock) {
          currentBlock.replaceWith(newBlock)
        }
      })
    }

    OptionHasUsefulMetafields(option) {
      if (!option) return false

      return Boolean(
        option.dataset.model ||
        option.dataset.tailShape ||
        option.dataset.lengthIn ||
        option.dataset.widthIn ||
        option.dataset.thicknessIn ||
        option.dataset.volume ||
        option.dataset.type ||
        option.dataset.lengthLabel ||
        option.dataset.idSurfboardmodel ||
        option.dataset.idWarehouse ||
        option.dataset.constructionType ||
        option.dataset.stocktype
      )
    }

    UpdateUrlHistory(productHandle) {
      const urlParams = new URLSearchParams(window.location.search)
      let newurl = `${window.location.protocol}//${window.location.host}/products/${productHandle}`

      if (urlParams.toString() !== "") {
        newurl += `?${urlParams.toString()}`
      }

      window.history.replaceState({ path: newurl }, "", newurl)
    }
  }

  customElements.define("surfboard-variants", SurfboardVariants)
}
